import { Component, DestroyRef, Input, output, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FileModel } from '@core/models/file.model';
import { FileService } from '@core/services/file.service';
import { getToken } from '@core/utils/getToken';
import { saveAs } from 'file-saver';
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2';

import { environment } from '../../../../environments/environment';
import { ContextMenuItemComponent } from '../context-menu/context-menu-item/context-menu-item.component';
import { ContextMenuComponent } from '../context-menu/context-menu.component';

interface PreviewFileState {
  file: FileModel | null;
  isShowMenu: boolean;
  link: string;
}

@Component({
  selector: 'ux-preview-file',
  templateUrl: './preview-file.component.html',
  styleUrls: ['./preview-file.component.less'],
  imports: [ContextMenuComponent, ContextMenuItemComponent, NgClickOutsideDelayOutsideDirective],
  standalone: true,
})
export class PreviewFileComponent {
  private readonly fileService = inject(FileService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly token = getToken();

  readonly deleteFile = output<FileModel>();

  private readonly state = signal<PreviewFileState>({
    file: null,
    isShowMenu: false,
    link: '',
  });

  protected readonly isShowMenu = computed(() => this.state().isShowMenu);
  protected readonly currentFile = computed(() => this.state().file);
  protected readonly link = computed(() => this.state().link);

  @Input()
  set file(value: FileModel) {
    if (!value) {
      return;
    }

    const link = `${environment.apiUrl}files/${value.id}?projectId=${value.projectId}&access-token=${this.token}`;

    this.state.update(state => ({
      ...state,
      file: value,
      link,
    }));
  }

  onToggleMenu(): void {
    this.state.update(state => ({
      ...state,
      isShowMenu: !state.isShowMenu,
    }));
  }

  onCloseMenu(): void {
    this.state.update(state => ({
      ...state,
      isShowMenu: false,
    }));
  }

  onDownloadFile(): void {
    const file = this.state().file;
    const link = this.state().link;

    if (!file || !link) {
      return;
    }

    this.fileService
      .getBlobFromUrl(link)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => saveAs(response, file.originalName));
  }

  onDeleteFile(): void {
    if (this.currentFile()) {
      this.deleteFile.emit(this.currentFile());
      this.onCloseMenu();
    }
  }
}
